import { OrderDetail } from '@insurance/api/requests'
import { getEffectiveDateRange } from '@insurance/utils'
import { usePermissionRestriction } from '@shared/hooks'
import { Card, Col, Divider, Form, Modal, Row } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AccidentInsuranceProvider } from './AccidentInsuranceContext'
import { AccidentInsuranceControls } from './AccidentInsuranceControls'
import { CustomerStep } from './CustomerStep'
import { Details } from './Details'
import { MainStep } from './MainStep'
import { PersonsStep } from './PersonsStep'
import {
  ContractTypeCode,
  ProfessionalCode,
  SportsCode,
  TerritoryCode
} from './type'
import {
  formProps,
  getAccidentValuesFromStorage,
  removeAccidentValuesFromStorage,
  saveAccidentValuesToStorage
} from './utils'
import {
  iniMixtContractorValues,
  serializeContractorFields
} from '../PersonsFields'

type Props = {
  order?: OrderDetail
}

const forms = [
  <MainStep key={0} />,
  <PersonsStep key={1} />,
  <CustomerStep key={2} />
]

const {
  stepKey,
  insuredAmountProps,
  contractTypeProps,
  territoryProps,
  sportRiskProps,
  professionalRiskProps,
  risksCoveredProps,
  ancillaryRisksCoveredProps,
  durationProps,
  priceKey,
  orderKey,
  validFromProps,
  contractorProps,
  sameContractorKey,
  halfDayCoverageProps,
  prevContractTypeKey,
  effectiveDateProps
} = formProps()

const initValues = {
  [stepKey.name]: 0,
  [priceKey.name]: undefined,
  [orderKey.name]: undefined,
  [insuredAmountProps.name]: 1000,
  [contractTypeProps.name]: ContractTypeCode.INDIVIDUAL,
  [territoryProps.name]: TerritoryCode.NATIONAL,
  [sportRiskProps.name]: SportsCode.NO_RISK,
  [professionalRiskProps.name]: ProfessionalCode.LOW_RISK,
  [risksCoveredProps.name]: undefined,
  [ancillaryRisksCoveredProps.name]: undefined,
  [durationProps.name]: undefined,
  [validFromProps.name]: dayjs(),
  [contractorProps.name]: iniMixtContractorValues.contractor,
  [sameContractorKey.name]: true,
  [halfDayCoverageProps.name]: false,
  [effectiveDateProps.name]: undefined
}

export const AccidentInsurance = ({ order }: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isReady, setIsReady] = useState(false)
  const { hasPermission } = usePermissionRestriction()

  const handleReadyToSaveData = () => setIsReady(true)

  const saveOnChangeValue = () => {
    const values = form.getFieldsValue(true)

    saveAccidentValuesToStorage(values, order?.id)

    return null
  }

  const formattedInitValues = useMemo(() => {
    const detailValues = order?.accidents

    if (!detailValues) return initValues

    const effectiveDateRange = getEffectiveDateRange(order)

    serializeContractorFields({ hasPermission }, detailValues)

    return {
      ...initValues,
      ...detailValues,
      ...(detailValues.valid_from && {
        valid_from: dayjs(detailValues.valid_from)
      }),
      effective_date: effectiveDateRange.minDate,
      persons: detailValues.persons?.map((person) => ({
        ...person,
        date_of_birth: dayjs(person.date_of_birth)
      })),
      sport: detailValues.sport?.code,
      profession: detailValues.profession?.code,
      risks: detailValues.risks
        ?.filter((risk) => risk?.risk?.is_primary)
        ?.map((risk) => ({
          ...risk,
          risk: risk?.risk?.code
        })),
      ancillary_risks: detailValues.risks
        ?.filter((risk) => !risk?.risk?.is_primary)
        ?.map((risk) => ({
          ...risk,
          risk: risk?.risk?.code
        })),

      customer: order.user,
      [prevContractTypeKey.name]: detailValues.contract_type
    }
  }, [order])

  useEffect(() => {
    form.setFieldsValue({
      ...formattedInitValues,
      ...getAccidentValuesFromStorage(order?.id)
    })
  }, [form, formattedInitValues, order?.id])

  useEffect(() => {
    const values = getAccidentValuesFromStorage(order?.id)
    if (!values) return

    const modal = Modal.confirm({
      onCancel: () => {
        removeAccidentValuesFromStorage(order?.id)
        setIsReady(false)
        form.resetFields()
        form.setFieldsValue(formattedInitValues)
      },
      title: t('have_saved_data'),
      onOk: () => setIsReady(true)
    })

    return () => modal.destroy()
  }, [form, t, order?.id, formattedInitValues])

  return (
    <AccidentInsuranceProvider isEditable={!!order} order={order}>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleReadyToSaveData}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={18}>
            <Form.Item shouldUpdate noStyle>
              {() => <Card>{forms[form.getFieldValue(stepKey.name)]}</Card>}
            </Form.Item>
          </Col>

          <Col xs={24} lg={6}>
            <Card>
              <Details />
            </Card>
          </Col>
        </Row>

        <Divider />

        <AccidentInsuranceControls
          steps={forms.length}
          onReadyToSaveData={handleReadyToSaveData}
        />

        {/* use this because when change step and other preserve values doesn't trigger onValuesChange */}
        {isReady && (
          <Form.Item shouldUpdate noStyle>
            {saveOnChangeValue}
          </Form.Item>
        )}
      </Form>
    </AccidentInsuranceProvider>
  )
}
